/**
 * Shared plumbing for reading a live playback level off a Web Audio graph.
 *
 * Every assistant-audio path that can be tapped uses these, so the analyser is
 * configured identically and the RMS maths lives in one place: the scenario
 * player's PCM stream, its intro clip, and the translator's playback saga.
 */

const ANALYSER_FFT_SIZE = 256;
const ANALYSER_SMOOTHING = 0.3;

/**
 * Build an analyser for tapping a graph. Returns `undefined` rather than throwing —
 * a level meter is a nicety and must never be able to break audio playback.
 */
export const createLevelAnalyser = (context: BaseAudioContext): AnalyserNode | undefined => {
  try {
    const analyser = context.createAnalyser();
    analyser.fftSize = ANALYSER_FFT_SIZE;
    analyser.smoothingTimeConstant = ANALYSER_SMOOTHING;
    return analyser;
  } catch {
    return undefined;
  }
};

/**
 * Linear RMS of whatever is currently passing through `analyser`.
 * Byte time-domain data is centred on 128, so samples are offset before squaring.
 */
export const readAnalyserRms = (analyser: AnalyserNode, buffer: Uint8Array): number => {
  analyser.getByteTimeDomainData(buffer);
  let sumSquares = 0;
  for (let index = 0; index < buffer.length; index += 1) {
    const sample = (buffer[index] - 128) / 128;
    sumSquares += sample * sample;
  }
  return Math.sqrt(sumSquares / buffer.length);
};

export type AnalyserLevelSource = {
  attach: (analyser: AnalyserNode) => void;
  /** Detach, but only if `analyser` is still the attached one. */
  detach: (analyser: AnalyserNode) => void;
  /**
   * Linear RMS of the attached analyser, or `undefined` while nothing is
   * attached. A genuine 0 (digital silence between words, or the padding at the
   * head and tail of a synthesized clip) is not the same as "no analyser", so
   * callers must branch on `undefined` rather than on falsiness.
   */
  read: () => number | undefined;
};

/**
 * A holder for "whichever analyser is live right now", so code that owns the
 * audio (a saga, a callback) can publish a level that a component reads each
 * frame without either side knowing about the other.
 */
export const createAnalyserLevelSource = (): AnalyserLevelSource => {
  let current: AnalyserNode | null = null;
  let buffer: Uint8Array | null = null;

  return {
    attach(analyser) {
      current = analyser;
      buffer = new Uint8Array(analyser.fftSize);
    },
    detach(analyser) {
      if (current === analyser) {
        current = null;
        buffer = null;
      }
    },
    read() {
      if (!current || !buffer) {
        return undefined;
      }
      return readAnalyserRms(current, buffer);
    }
  };
};
